import type {
	LoanRole,
	LoanUser,
	UserDraft,
	UserLoan,
} from '@matech/thebigpos-sdk'
import {
	useQuery,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { captureException } from '@sentry/react'

import { useAlert } from './useAlert'
import { PaginationParams } from '../types'
import queryKeys from '../services/queryKeys'
import { concatStrings } from '../services/helpers'
import { TheBigPOSApi } from '../lib/TheBigPOSClient'
import { UserRole } from '../components/modals/ModalShareLoan'
import type { LoanUserRow } from '../components/loanAppDetails/LoanUsersTable'
import mapLoanUserOrUserDraftToUserRole from '../pages/protected/pipeline/utils/mapLoanUserOrUserDraftToUserRole'

export type UseShareLoanProps = {
	loanId?: string
	isDraft?: boolean
	paginationParams: PaginationParams
	queryDisabled?: boolean
}

type UpdateSharedUserCacheParams = {
	userId: string
	loadingEdit?: boolean
	loadingRevoke?: boolean
}

type SharedUserBaseParams = {
	userId: string
	updateCache?: boolean
	showToast?: boolean
}

type AddSharedUserParams = SharedUserBaseParams & {
	loanRole: LoanRole
}

type RevokeSharedUserParams = SharedUserBaseParams & {
	allowLoading?: boolean
}

type UpdateSharedUserParams = SharedUserBaseParams & {
	role: LoanRole
}

export const LOAN_ROLES_TO_EXCLUDE_FROM_SHARED_USERS = [
	'Borrower',
	'CoBorrower',
	'NonBorrower',
]

function isUserDraft(data: UserDraft | LoanUser): data is UserDraft {
	return (data as UserDraft).draftID !== undefined
}

const filterAndMapSharedUsersResponse = (
	data: UserLoan[] | UserDraft[]
) => {
	return data
		.filter(
			(item) =>
				!LOAN_ROLES_TO_EXCLUDE_FROM_SHARED_USERS.includes(item.role)
		)
		.map((userLoan) => mapLoanUserOrUserDraftToUserRole(userLoan))
		.sort((a, b) => a.user.name.localeCompare(b.user.name))
}

/**
 * Hook for managing shared users on a loan or loan draft.
 *
 * Provides query and mutation utilities for fetching, adding, revoking, and updating
 * users with assigned roles on a loan. Handles cache updates and loading states.
 *
 * @param {Object} params - Hook parameters.
 * @param {string} [params.loanId] - The loan or draft ID.
 * @param {boolean} [params.isDraft] - Whether the loan is a draft.
 * @param {PaginationParams} params.paginationParams - Pagination parameters for fetching users.
 * @param {boolean} [params.queryDisabled] - If true, disables the initial query.
 *
 * @returns {{
 *   sharedUsersQuery: UseQueryResult<UserRole[], Error>,
 *   updateSharedUserRole: (params: UpdateSharedUserParams) => Promise<void>,
 *   addSharedUserMutation: UseMutationResult<any, Error, AddSharedUserParams, unknown>,
 *   revokeSharedUserMutation: UseMutationResult<any, Error, RevokeSharedUserParams, unknown>
 * }}
 *
 * @description
 * **Default Behavior:**
 * - `showToast`: Defaults to `true` - Success and error toast notifications will be displayed by default.
 * - `updateCache`: Cache updates are **NOT** performed by default unless explicitly set to `true`.
 *
 * This means that mutations will show toast notifications but won't update the local cache
 * unless you specifically pass `updateCache: true` in the mutation parameters.
 *
 * **Returned Properties:**
 * - `sharedUsersQuery`: TanStack Query result object with `data`, `isLoading`, `error`, `refetch`, etc.
 * - `updateSharedUserRole`: Function to update a user's role (combines revoke + add operations)
 * - `addSharedUserMutation`: TanStack Query mutation object for adding users
 * - `revokeSharedUserMutation`: TanStack Query mutation object for revoking user access
 *
 * @example
 * const {
 *   sharedUsersQuery,
 *   updateSharedUserRole,
 *   addSharedUserMutation,
 *   revokeSharedUserMutation
 * } = useShareLoan({ loanId, isDraft, paginationParams });
 *
 * // Access shared users data
 * const sharedUsers = sharedUsersQuery.data;
 * const isLoading = sharedUsersQuery.isLoading;
 *
 * // Add user with toast but no cache update (default behavior)
 * await addSharedUserMutation.mutateAsync({ userId: '123', loanRole: 'Viewer' });
 *
 * // Add user with both toast and cache update
 * await addSharedUserMutation.mutateAsync({
 *   userId: '123',
 *   loanRole: 'Viewer',
 *   updateCache: true
 * });
 *
 * // Update user role
 * await updateSharedUserRole({
 *   userId: '123',
 *   role: 'Editor',
 *   updateCache: true
 * });
 *
 * // Revoke user access
 * await revokeSharedUserMutation.mutateAsync({
 *   userId: '123',
 *   updateCache: true
 * });
 */
const useShareLoan = ({
	loanId,
	isDraft,
	paginationParams,
	queryDisabled,
}: UseShareLoanProps) => {
	const { alert } = useAlert()
	const queryClient = useQueryClient()
	const { formatMessage } = useIntl()

	const queryKey = [queryKeys.sharedUsers, loanId]
	const queryEnabled = !!loanId && !queryDisabled

	const sharedUsersQuery = useQuery({
		queryKey,
		queryFn: async () => {
			if (!loanId) throw new Error('loanId was not provided')

			if (isDraft) {
				const response = await TheBigPOSApi.getDraftUsers(
					loanId,
					paginationParams
				)

				return filterAndMapSharedUsersResponse(response.data.rows)
			}

			const response = await TheBigPOSApi.getLoan(loanId)

			return filterAndMapSharedUsersResponse(response.data.userLoans)
		},
		enabled: queryEnabled,
	})

	const includeSharedUserInCache = (
		user: LoanUserRow,
		role: string
	) => {
		queryClient.setQueryData<UserRole[] | undefined>(
			queryKey,
			(prevData) => {
				const newUser: UserRole = {
					user,
					assignedRole: role,
				}

				if (!prevData) return [newUser]

				const exists = prevData.some(
					(item) => item.user.id === user.id
				)

				if (exists) return prevData

				return [...prevData, newUser]
			}
		)
	}

	const removeSharedUserFromCache = (userId: string) => {
		queryClient.setQueryData<UserRole[] | undefined>(
			queryKey,
			(prevData) => {
				return prevData?.filter((item) => item.user.id !== userId)
			}
		)
	}

	const updateSharedUserLoadingCache = ({
		userId,
		loadingEdit,
		loadingRevoke,
	}: UpdateSharedUserCacheParams) => {
		queryClient.setQueryData<UserRole[] | undefined>(
			queryKey,
			(prevData) => {
				if (!prevData) return undefined

				return prevData.map((item) => {
					if (item.user.id !== userId) return item

					const updatedItem = { ...item }

					if (loadingEdit !== undefined)
						updatedItem.loadingEdit = loadingEdit

					if (loadingRevoke !== undefined)
						updatedItem.loadingRevoke = loadingRevoke

					return updatedItem
				})
			}
		)
	}

	const getSharedUserFromCache = (userId: string) => {
		const sharedUserList = queryClient.getQueryData<
			UserRole[] | undefined
		>(queryKey)
		if (!sharedUserList) return undefined

		return sharedUserList.find((item) => item.user.id === userId)
	}

	const addSharedUserMutation = useMutation({
		mutationFn: async ({ userId, loanRole }: AddSharedUserParams) => {
			if (!loanId) throw new Error('loanId was not provided')

			if (isDraft)
				return TheBigPOSApi.addDraftUsers(loanId, userId, {
					loanRole,
				})

			return TheBigPOSApi.addLoanUser(loanId, userId, {
				loanRole,
			})
		},
		onSuccess: (
			{ data },
			{ updateCache, showToast = true, loanRole }
		) => {
			if (updateCache) {
				let loanUserRow: LoanUserRow

				if (isUserDraft(data)) {
					const { user } = data
					loanUserRow = {
						id: user.id,
						name: concatStrings(
							user.firstName,
							user.lastName
						).getValue(),
						email: user.email,
						role: user.role as LoanRole,
						phone: user.phone,
						shared: true,
						createdAt: user.createdAt,
					}
				} else {
					loanUserRow = {
						id: data.id,
						name: concatStrings(
							data.firstName,
							data.lastName
						).getValue(),
						email: data.email,
						role: data.role as LoanRole,
						phone: data.phone,
						shared: true,
						createdAt: data.createdAt,
					}
				}

				includeSharedUserInCache(loanUserRow, loanRole)
			}

			if (showToast)
				alert(formatMessage({ id: 'alerts.share_loan_success' }), {
					severity: 'success',
				})
		},
		onError: (error) => {
			captureException(error)
			alert(formatMessage({ id: 'alerts.share_loan_failed' }), {
				severity: 'error',
			})
		},
	})

	const revokeSharedUserMutation = useMutation({
		mutationFn: async ({ userId }: RevokeSharedUserParams) => {
			if (!loanId) throw new Error('loanId was not provided')

			if (isDraft) {
				return TheBigPOSApi.deleteDraftUser(loanId, userId)
			}

			return TheBigPOSApi.removeLoanUser(loanId, userId)
		},
		onMutate: ({ userId, allowLoading = true }) => {
			updateSharedUserLoadingCache({
				userId,
				loadingRevoke: allowLoading,
			})
		},
		onSuccess: (_, { userId, updateCache, showToast = true }) => {
			const revokedUser = getSharedUserFromCache(userId)
			const revokedUserName = revokedUser?.user.name

			if (updateCache) removeSharedUserFromCache(userId)

			if (showToast && revokedUserName)
				alert(
					formatMessage(
						{ id: 'alerts.revoke_loan_success' },
						{ name: revokedUserName }
					),
					{
						severity: 'success',
					}
				)
		},
		onError: (error) => {
			captureException(error)
		},
		onSettled: (_, __, { userId }) => {
			updateSharedUserLoadingCache({
				userId,
				loadingRevoke: false,
			})
		},
	})

	const updateSharedUserCache = (user: UserRole) => {
		queryClient.setQueryData<UserRole[] | undefined>(
			queryKey,
			(prevData) => {
				if (!prevData) return undefined

				return prevData.map((item) =>
					item.user.id === user.user.id ? { ...user } : item
				)
			}
		)
	}

	const updateSharedUserRole = async ({
		userId,
		role,
		updateCache,
		showToast = true,
	}: UpdateSharedUserParams) => {
		let prevSharedUser: UserRole | undefined

		try {
			if (!loanId) throw new Error('loanId was not provided')

			if (updateCache) {
				prevSharedUser = getSharedUserFromCache(userId)

				if (!prevSharedUser)
					throw new Error(
						'User not found in list to get previous data'
					)
			}

			updateSharedUserLoadingCache({
				userId,
				loadingEdit: true,
			})

			await revokeSharedUserMutation.mutateAsync({
				userId,
				showToast: false,
				allowLoading: false,
				updateCache: false,
			})

			await addSharedUserMutation.mutateAsync({
				userId,
				loanRole: role,
				showToast: false,
				updateCache: false,
			})

			if (showToast && prevSharedUser)
				alert(
					formatMessage(
						{ id: 'alerts.role_update_success' },
						{ name: prevSharedUser.user.name }
					),
					{
						severity: 'success',
					}
				)

			if (updateCache && prevSharedUser)
				updateSharedUserCache({
					...prevSharedUser,
					assignedRole: role,
					loadingEdit: false,
				})
		} catch (error) {
			captureException(error)

			if (!showToast) throw error

			alert(
				formatMessage(
					{ id: 'alerts.role_update_failed' },
					{
						name: prevSharedUser?.user.name,
					}
				),
				{
					severity: 'error',
				}
			)
		} finally {
			updateSharedUserLoadingCache({
				userId,
				loadingEdit: false,
			})
		}
	}

	return {
		sharedUsersQuery,
		updateSharedUserRole,
		addSharedUserMutation,
		revokeSharedUserMutation,
	}
}

export default useShareLoan
